// SCR-STAFF-107 — پرونده‌های ناقص (AP-PAT-02، US-PAT-007): ثبت موقت (نام، جنسیت و سن تقریبی؛ نام خانوادگی اختیاری)، صف پرونده‌های
// موقت با علامت «دیرکرد» پس از روزهای تنظیم‌شده، و تکمیل به پرونده کامل (موبایل اصلی، نام خانوادگی و کد ملی).
import { zodResolver } from '@hookform/resolvers/zod';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import {
  unwrap,
  type IncompletePatientItem,
  type PatientView,
  type RegisterPatientRequest,
  type TemporaryRegistrationRequest,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  isValidNationalId,
  ListPage,
  MobileFieldRHF,
  NationalIdFieldRHF,
  SelectRHF,
  TextFieldRHF,
  toJalaliDateTime,
  zMobile,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router';
import { z } from 'zod';
import { formatJalaliInput, parseJalaliInput, patientPath, patKeys } from './common';

export const SCR_STAFF_107 = 'SCR-STAFF-107';

const temporarySchema = z.object({
  firstName: zRequiredText(100),
  lastName: z.string().max(100, { error: 'common:validation.too_long' }),
  gender: z.enum(['Female', 'Male']),
  approximateAge: z
    .string()
    .refine((value) => value.trim() === '' || (/^\d{1,3}$/.test(value.trim()) && Number(value) <= 130), { error: 'pat:validation.age' }),
});
type TemporaryForm = z.input<typeof temporarySchema>;

/** دیالوگ ثبت موقت (US-PAT-007). */
export function TemporaryRegisterDialog({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (patient: PatientView) => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, reset, setError } = useForm<TemporaryForm>({
    resolver: zodResolver(temporarySchema),
    defaultValues: { firstName: '', lastName: '', gender: 'Female', approximateAge: '' },
  });
  useEffect(() => {
    if (open) {
      reset();
      setSubmitError(undefined);
    }
  }, [open, reset]);
  const save = useMutation({
    mutationFn: (body: TemporaryRegistrationRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients:register-temporary', {
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (patient) => {
      void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'incomplete'] });
      onCreated(patient);
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, { fields: ['firstName', 'lastName', 'gender', 'approximateAge'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="pat:temporary.title"
      screenId="SCR-STAFF-101-TEMPORARY"
      onSubmit={() =>
        void handleSubmit((form) =>
          save.mutate({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim() || null,
            gender: form.gender,
            approximateAge: form.approximateAge.trim() ? Number(form.approximateAge) : null,
          }),
        )()
      }
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
      submitKey="pat:temporary.submit"
    >
      <TextFieldRHF control={control} name="firstName" labelKey="pat:fields.first_name" required fullWidth autoFocus />
      <TextFieldRHF control={control} name="lastName" labelKey="pat:fields.last_name" helperKey="pat:temporary.last_name_help" fullWidth />
      <SelectRHF
        control={control}
        name="gender"
        labelKey="pat:fields.gender"
        required
        fullWidth
        options={[
          { value: 'Female', labelKey: 'pat:gender.Female' },
          { value: 'Male', labelKey: 'pat:gender.Male' },
        ]}
      />
      <TextFieldRHF
        control={control}
        name="approximateAge"
        labelKey="pat:temporary.approximate_age"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}

const completeSchema = z
  .object({
    firstName: zRequiredText(100),
    lastName: zRequiredText(100),
    gender: z.enum(['Female', 'Male']),
    mobile: zMobile(),
    nationalId: z.string(),
    birthDate: z.string(),
  })
  .superRefine((value, context) => {
    if (value.nationalId && !isValidNationalId(value.nationalId)) {
      context.addIssue({ code: 'custom', path: ['nationalId'], message: 'common:validation.national_id' });
    }
    if (value.birthDate.trim() && !parseJalaliInput(value.birthDate)) {
      context.addIssue({ code: 'custom', path: ['birthDate'], message: 'pat:validation.birth_date' });
    }
  });
type CompleteForm = z.input<typeof completeSchema>;

/** تکمیل پرونده موقت (US-PAT-007، AC-PAT-007.2). */
export function CompletePatientDialog({ patient, onClose }: { patient: PatientView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, reset, setError } = useForm<CompleteForm>({
    resolver: zodResolver(completeSchema),
    defaultValues: { firstName: '', lastName: '', gender: 'Female', mobile: '', nationalId: '', birthDate: '' },
  });
  useEffect(() => {
    if (patient) {
      setSubmitError(undefined);
      reset({
        firstName: patient.firstName,
        lastName: patient.lastName,
        gender: patient.gender,
        mobile: '',
        nationalId: '',
        birthDate: patient.birthDateEstimated ? '' : formatJalaliInput(patient.birthDate),
      });
    }
  }, [patient, reset]);
  const save = useMutation({
    mutationFn: (body: RegisterPatientRequest) =>
      unwrap(tenantApi.POST('/api/v1/patients/patients/{id}:complete', { params: { path: { id: patient?.id ?? '' } }, body })),
    onSuccess: (updated) => {
      queryClient.setQueryData(patKeys.patient(session?.tenantId, updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: patKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['firstName', 'lastName', 'gender', 'mobile', 'nationalId', 'birthDate'],
        codeFields: { 'PHX-PAT-0101': 'nationalId', 'PHX-PAT-0301': 'nationalId', 'PHX-PAT-0102': 'birthDate' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open={patient !== null}
      titleKey="pat:temporary.complete"
      screenId="SCR-STAFF-107-COMPLETE"
      onSubmit={() =>
        void handleSubmit((form) =>
          save.mutate({
            firstName: form.firstName,
            lastName: form.lastName,
            gender: form.gender,
            mobile: form.mobile,
            nationalId: form.nationalId || null,
            birthDate: parseJalaliInput(form.birthDate) ?? null,
            confirmDistinct: true,
          }),
        )()
      }
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <MobileFieldRHF control={control} name="mobile" labelKey="pat:fields.mobile" required fullWidth />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextFieldRHF control={control} name="firstName" labelKey="pat:fields.first_name" required fullWidth />
        <TextFieldRHF control={control} name="lastName" labelKey="pat:fields.last_name" required fullWidth />
      </Stack>
      <SelectRHF
        control={control}
        name="gender"
        labelKey="pat:fields.gender"
        required
        fullWidth
        options={[
          { value: 'Female', labelKey: 'pat:gender.Female' },
          { value: 'Male', labelKey: 'pat:gender.Male' },
        ]}
      />
      <NationalIdFieldRHF control={control} name="nationalId" labelKey="pat:fields.national_id" fullWidth />
      <TextFieldRHF
        control={control}
        name="birthDate"
        labelKey="pat:fields.birth_date"
        helperKey="pat:fields.birth_date_help"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}

export function IncompletePatientsPage() {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const navigate = useNavigate();
  const create = useWriteAccess('pat.patient.create');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [registering, setRegistering] = useState(false);
  const incomplete = useQuery({
    queryKey: patKeys.incomplete(session?.tenantId, overdueOnly),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/incomplete', { params: { query: { overdueOnly } } })),
  });
  const columns: DataTableColumn<IncompletePatientItem>[] = [
    {
      id: 'name',
      headerKey: 'pat:fields.first_name',
      cell: (row) => <RouterLink to={patientPath(row.patient.id)}>{row.patient.displayName}</RouterLink>,
    },
    { id: 'file', headerKey: 'pat:profile.file_number', cell: (row) => <bdi dir="ltr">{faDigits(row.patient.fileNumber)}</bdi> },
    { id: 'created', headerKey: 'pat:profile.registered_at', cell: (row) => toJalaliDateTime(row.createdAt) },
    {
      id: 'overdue',
      headerKey: 'pat:temporary.overdue_label',
      cell: (row) => (row.overdue ? <Chip size="small" color="warning" label={t('pat:temporary.overdue')} data-testid="incomplete-overdue" /> : null),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_107}
      titleKey="pat:temporary.queue"
      subtitleKey="pat:temporary.queue_subtitle"
      actions={
        create.canWrite ? (
          <Button variant="contained" startIcon={<PersonAddAltOutlinedIcon />} onClick={() => setRegistering(true)} data-testid="temporary-register">
            {t('pat:temporary.title')}
          </Button>
        ) : undefined
      }
      filters={
        <FormControlLabel
          control={<Switch checked={overdueOnly} onChange={(event) => setOverdueOnly(event.target.checked)} />}
          label={t('pat:temporary.overdue_only', { days: faDigits(incomplete.data?.overdueAfterDays ?? 7) })}
        />
      }
    >
      <DataTable
        captionKey="pat:temporary.queue"
        columns={columns}
        rows={incomplete.data?.items}
        isLoading={incomplete.isPending}
        error={incomplete.error}
        onRetry={() => void incomplete.refetch()}
        getRowId={(row) => row.patient.id}
        onRowClick={(row) => navigate(patientPath(row.patient.id))}
        emptyTitleKey="pat:temporary.empty"
      />
      <TemporaryRegisterDialog
        open={registering}
        onClose={() => setRegistering(false)}
        onCreated={(patient) => {
          setRegistering(false);
          navigate(patientPath(patient.id));
        }}
      />
    </ListPage>
  );
}
